// src/pages/RoleManagement/modals/EditRoleModal.tsx
// Edit existing role group modal

import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Alert,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

// Components
import ColorPicker from "../components/ColorPicker";

// Redux
import { closeModal } from "slices/roleManagement/reducer";
import { updateExistingRoleGroup } from "slices/roleManagement/thunk";

// Types
import { RoleGroupDetail } from "../types";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('role_management');

interface EditRoleModalProps {
  isOpen: boolean;
  toggle: () => void;
  roleGroup: RoleGroupDetail | null;
  onSuccess?: () => void;
}

const EditRoleModal: React.FC<EditRoleModalProps> = ({
  isOpen,
  toggle,
  roleGroup,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const location = useLocation();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: roleGroup?.name || "",
      description: roleGroup?.description || "",
      color: roleGroup?.color || "",
      isProtected: roleGroup?.isProtected || false,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .required(st('ten_la_bat_buoc_error'))
        .min(3, st('ten_phai_co_it_nhat_3_error')),
      description: Yup.string()
        .required(st('mo_ta_la_bat_buoc_error'))
        .min(10, st('mo_ta_phai_co_it_nhat_error')),
      color: Yup.string().required(st('vui_long_chon_mau_error')),
    }),
    onSubmit: async (values) => {
      if (!roleGroup) return;

      try {
        await dispatch(
          updateExistingRoleGroup({
            id: roleGroup.id,
            data: {
              id: roleGroup.id,
              code: roleGroup.code,
              name: values.name,
              description: values.description,
              color: values.color,
              isDefault: roleGroup.isDefault,
              isProtected: values.isProtected,
            },
          }),
        ).unwrap();
        toast.success(st('cap_nhat_nhom_quyen_thanh_cong_message'));
        validation.resetForm();
        // Backend UpdateRole chỉ trả về long (id), không đủ dữ liệu để patch state.roleGroups —
        // phải load lại danh sách thì mới thấy thay đổi ngay, không cần F5.
        onSuccess?.();
      } catch (error: any) {
        toast.error(error || st('co_loi_xay_ra_khi_cap_message_2'));
      }
    },
  });

  const handleClose = () => {
    validation.resetForm();
    toggle();
  };

  if (!roleGroup) return null;

  const isSystemRole = roleGroup.isProtected;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size="lg">
      <ModalHeader toggle={handleClose}>{st('chinh_sua_nhom_quyen_title')}</ModalHeader>
      <Form onSubmit={validation.handleSubmit}>
        <ModalBody>
          {/* System Role Warning */}
          {isSystemRole && (
            <Alert color="warning" className="mb-3">
              <i className="ri-alert-line me-2"></i>
              <strong>{st('quyen_text')}</strong> {st('khong_duoc_phep_chinh_sua_message')}
            </Alert>
          )}

          {/* Code (Read-only) */}
          <FormGroup>
            <Label for="code">{st('code_label')}</Label>
            <Input
              type="text"
              id="code"
              value={roleGroup.code}
              data-action-code="edit-input-code"
              data-action-name="Code nhóm quyền"
              disabled
              className="bg-light"
            />
            <small className="text-muted">{st('code_cannot_be_changed_hint')}</small>
          </FormGroup>

          {/* Name */}
          <FormGroup>
            <Label for="name">{st('ten_label')}</Label>
            <Input
              type="text"
              id="name"
              name="name"
              placeholder={st('nhap_ten_nhom_quyen_placeholder')}
              value={validation.values.name}
              data-action-code="edit-input-name"
              data-action-name="Tên nhóm quyền"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              disabled={
                isSystemRole ||
                !PageActionPermission.canEnable(
                  location.pathname,
                  "edit-input-name",
                )
              }
              invalid={
                validation.touched.name && validation.errors.name ? true : false
              }
            />
            {validation.touched.name && validation.errors.name ? (
              <FormFeedback type="invalid">
                {validation.errors.name}
              </FormFeedback>
            ) : null}
          </FormGroup>

          {/* Description */}
          <FormGroup>
            <Label for="description">{st('mo_ta_label')}</Label>
            <Input
              type="textarea"
              id="description"
              name="description"
              placeholder={st('mo_ta_nhom_quyen_va_trach_placeholder')}
              rows={3}
              value={validation.values.description}
              data-action-code="edit-input-description"
              data-action-name="Mô tả nhóm quyền"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              disabled={
                isSystemRole ||
                !PageActionPermission.canEnable(
                  location.pathname,
                  "edit-input-description",
                )
              }
              invalid={
                validation.touched.description && validation.errors.description
                  ? true
                  : false
              }
            />
            {validation.touched.description && validation.errors.description ? (
              <FormFeedback type="invalid">
                {validation.errors.description}
              </FormFeedback>
            ) : null}
          </FormGroup>

          {/* Color Picker */}
          <FormGroup>
            <ColorPicker
              value={validation.values.color}
              onChange={(color) => validation.setFieldValue("color", color)}
              disabled={isSystemRole}
            />
            {validation.touched.color && validation.errors.color ? (
              <div className="text-danger mt-1">
                <small>{validation.errors.color}</small>
              </div>
            ) : null}
          </FormGroup>

          {/* IsProtected Checkbox */}
          <FormGroup check className="mb-3">
            <Input
              type="checkbox"
              id="isProtected"
              name="isProtected"
              checked={validation.values.isProtected}
              data-action-code="edit-input-isprotected"
              data-action-name="Quyền bảo vệ (IsProtected)"
              onChange={validation.handleChange}
              disabled={
                isSystemRole ||
                !PageActionPermission.canEnable(
                  location.pathname,
                  "edit-input-isprotected",
                )
              }
            />
            <Label check htmlFor="isProtected" className="ms-2">
              {st('quyen_bao_ve_admin_toan_quyen_label')}
            </Label>
            {validation.values.isProtected && !isSystemRole && (
              <Alert color="warning" className="mt-2 mb-0">
                <i className="ri-alert-line me-2"></i>
                {st('sau_khi_luu_vai_tro_nay_message')} <strong>{st('khong_the_chinh_sua_lai_text')}</strong>{" "}
                {st('qua_giao_dien_nay_message')}
              </Alert>
            )}
          </FormGroup>

          {/* Member Count Info */}
          {roleGroup.userCount > 0 && (
            <Alert color="info" className="mb-3">
              <i className="ri-user-line me-2"></i>
              {st('nhom_quyen_nay_co_text')}{" "}
              <strong>{roleGroup.userCount} {st('thanh_vien_text')}</strong>
            </Alert>
          )}

          <Alert color="light" className="mb-0 border">
            <i className="ri-information-line me-2"></i>
            {st('viec_gan_module_permission_va_action_message')}
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            {st('huy_button')}
          </Button>
          {!isSystemRole && PageActionPermission.canDisplay(
            location.pathname,
            "button-edit-submit",
          ) && (
            <Button
              color="primary"
              type="submit"
              disabled={validation.isSubmitting}
              data-action-code="button-edit-submit"
              data-action-name="Lưu thay đổi nhóm quyền"
            >
              <i className="ri-save-line me-1"></i>
              {validation.isSubmitting ? st('dang_luu_text') : st('cap_nhat_nhom_quyen_text')}
            </Button>
          )}
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EditRoleModal;
